import Step from "../Steps/Step";
import InferenceChainStep from "../Steps/InferenceChainStep";
import ApiStep from "../Steps/ApiStep";
import CodeStep from "../Steps/CodeStep";
import { DEFAULT_MODEL, DEFAULT_TEMPERATURE } from "../../config/models";

interface CognitiveAbility {
  enabled: boolean;
  steps: any[];
}

interface CognitiveAbilities {
  learn: CognitiveAbility;
  retrieve: CognitiveAbility;
  reason: CognitiveAbility;
  design: CognitiveAbility;
  reflect: CognitiveAbility;
}

class ProceduralMemory {
  model: string;
  modelTemperature: number;
  steps: Step[];
  cognitiveAbilities: CognitiveAbilities;
  feedback: string;

  constructor(
    model: string = DEFAULT_MODEL, 
    modelTemperature: number = DEFAULT_TEMPERATURE, 
    steps: Step[] = [],
    cognitiveAbilities?: CognitiveAbilities,
    feedback: string = ''
  ) {
    this.model = model || DEFAULT_MODEL;
    this.modelTemperature = modelTemperature;
    this.steps = steps || [];
    this.cognitiveAbilities = cognitiveAbilities || {
      learn: { enabled: false, steps: [] },
      retrieve: { enabled: false, steps: [] },
      reason: { enabled: false, steps: [] },
      design: { enabled: false, steps: [] },
      reflect: { enabled: false, steps: [] }
    };
    this.feedback = feedback;
  }

  toJson() {
    const stepsJson = this.steps
      .filter(step => step !== null && typeof step.toJson === 'function')
      .map(step => {
        try {
          const stepJson = step.toJson();
          if (!stepJson || !stepJson.params || !stepJson.params.definition) {
            console.warn('Invalid step JSON structure:', stepJson);
            return null;
          }

          // Ensure all required fields are present
          const definition = { ...stepJson.params.definition };

          // Handle inference chain specific fields
          if (step instanceof InferenceChainStep) {
            definition.model = definition.model || DEFAULT_MODEL;
            definition.model_temp = definition.model_temp ?? DEFAULT_TEMPERATURE;
            definition.prompt = definition.prompt || '';
            definition.variables = definition.variables || {};
            definition.is_streaming = definition.is_streaming || false;
          }

          // Handle code step specific fields
          if (step instanceof CodeStep) {
            definition.code = definition.code || '';
            definition.variables = definition.variables || {};
          }

          return {
            type: stepJson.type,
            params: {
              id: stepJson.params.id,
              description: stepJson.params.description,
              definition: {
                ...definition,
                ...(definition.condition ? { condition: definition.condition } : {})
              }
            }
          };
        } catch (error) {
          console.error('Error converting step to JSON:', error, step);
          return null;
        }
      })
      .filter(Boolean);

    return {
      model: this.model || DEFAULT_MODEL,
      model_temp: parseFloat(this.modelTemperature?.toString() || DEFAULT_TEMPERATURE.toString()),
      learn: this.cognitiveAbilities.learn,
      retrieve: this.cognitiveAbilities.retrieve,
      reason: this.cognitiveAbilities.reason,
      design: this.cognitiveAbilities.design,
      reflect: this.cognitiveAbilities.reflect,
      feedback: this.feedback,
      steps: stepsJson
    };
  }

  static fromJson(json: any): ProceduralMemory {
    if (!json) {
      return new ProceduralMemory(DEFAULT_MODEL, DEFAULT_TEMPERATURE, [], undefined, '');
    }

    const steps = (json.steps || [])
      .filter(Boolean)
      .map(step => {
        if (!step || !step.type) return null;

        try {
          switch (step.type) {
            case 'inference_chain':
              return InferenceChainStep.fromJson(step);
            case 'code':
              return CodeStep.fromJson(step);
            case 'api':
              return ApiStep.fromJson(step);
            default:
              return Step.fromJson(step);
          }
        } catch (error) {
          console.error('Error parsing step:', error);
          return null;
        }
      })
      .filter(Boolean);

    // Extract cognitive abilities from JSON
    const cognitiveAbilities: CognitiveAbilities = {
      learn: json.learn || { enabled: false, steps: [] },
      retrieve: json.retrieve || { enabled: false, steps: [] },
      reason: json.reason || { enabled: false, steps: [] },
      design: json.design || { enabled: false, steps: [] },
      reflect: json.reflect || { enabled: false, steps: [] }
    };

    return new ProceduralMemory(
      json.model || DEFAULT_MODEL,
      json.model_temp || DEFAULT_TEMPERATURE,
      steps,
      cognitiveAbilities,
      json.feedback || ''
    );
  }
}

export default ProceduralMemory;